import queryString from 'query-string';
import { equals, omit, pathOr, prop, sortBy } from 'ramda';
import { array, mixed, number } from 'yup';

import { RUNTIME_DISPLAY_UNITS, RuntimeDisplayUnits } from '@frontend/api';

import { SERVER } from 'constants/all';

import { DISPLAY_UNITS_FIELD_NAMES } from 'modules/quotes/quoteItems/data/constants';

import { splitQuantityString, toCamelCase } from 'utils/stringUtils';

import { parseDecimalString } from './formatters';

export const getDigitalQuoteUrl = (
  token: string,
  groupId: string,
  {
    full,
    emptyDomainName,
    params = {},
  }: {
    full?: boolean;
    emptyDomainName?: boolean;
    params?: Record<string, unknown>;
  } = {},
) => {
  let url =
    SERVER === 'local'
      ? (full ? location.origin : '') + `/digi_quote/${token}`
      : SERVER === 'production'
      ? `${
          emptyDomainName ? '' : 'https://www.digital-quote.com'
        }/${groupId}/quotes/${token}`
      : `${
          emptyDomainName ? '' : `https://${SERVER}.digital-quote.com`
        }/${groupId}/quotes/${token}`;
  const paramsStr = queryString.stringify(params);
  if (paramsStr) {
    url += '?' + paramsStr;
  }
  return url;
};

export const isValidExpedite = (expedite) => {
  return Object.values(expedite).filter((x) => x === null).length === 0;
};

export const isCompleteExpedite = (expedite) => {
  return expedite.markup !== '' && expedite.daysFaster;
};

export const isExpediteEqual = (expedite1, expedite2) => {
  const withoutId = omit(['id']);
  return equals(withoutId(expedite1), withoutId(expedite2));
};

// NOTE: responsible for sorting component quantity arrays all over the app
// use this whenever rendering component quantities with a map
export const sortQuantities = (quantities) =>
  quantities.sort((a, b) => {
    if (a.quantity === b.quantity) {
      return a.id - b.id;
    } else {
      return a.quantity - b.quantity;
    }
  });

export const findAndSortCustomCostCategories = (quantities) => {
  const uniqueCategories = new Map();
  quantities.forEach((quantity) => {
    quantity.customCostCategories?.forEach((category) => {
      if (!uniqueCategories.has(category.name)) {
        uniqueCategories.set(category.name, {
          name: category.name,
          color: category.color,
        });
      }
    });
  });

  return Array.from(uniqueCategories.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

// customCostQuantities is different than sortedQuantities because it checks customCostCategories[].name
// customCostCategories might not exist on every quantity break, so this provides blanks in the data array as needed
export const createCustomCostQuantities = (
  sortedQuantities,
  sortedCustomCostCategories,
) =>
  sortedQuantities.map((quantity) => {
    const filledQuantity = { ...quantity };
    const filledCustomCostCategories = Array(
      sortedCustomCostCategories.length,
    ).fill(null);

    filledQuantity.customCostCategories?.forEach((category) => {
      const index = sortedCustomCostCategories.findIndex(
        (sortedCategory) => category.name === sortedCategory.name,
      );
      if (index < 0) {
        return;
      }

      filledCustomCostCategories[index] = category;
    });

    filledQuantity.customCostCategories = filledCustomCostCategories;
    return filledQuantity;
  });

export const convertHoursToMinutes = (value) => {
  return (value * 60).toFixed(2);
};

export const convertMinutesToHours = (value) => {
  return (value / 60.0).toFixed(10);
};

export const safeConvertMinutesToHours = (value) => {
  return parseFloat(value) ? convertMinutesToHours(parseFloat(value)) : value;
};

export const safeConvertHoursToMinutes = (value) => {
  return parseFloat(value) ? convertHoursToMinutes(parseFloat(value)) : value;
};

const convertToSeconds = (value: number, unit: RuntimeDisplayUnits): number => {
  switch (unit) {
    case RUNTIME_DISPLAY_UNITS.HOURS:
      return value * 60 * 60;
    case RUNTIME_DISPLAY_UNITS.MINUTES:
      return value * 60;
    case RUNTIME_DISPLAY_UNITS.SECONDS:
      return value;
    case RUNTIME_DISPLAY_UNITS.PARTS_PER_HOUR:
      // Convert parts per hour to seconds per part protecting against division by zero
      return value === 0 ? 0 : (60 * 60) / value;
    case RUNTIME_DISPLAY_UNITS.PARTS_PER_MINUTE:
      // Convert parts per minute to seconds per part protecting against division by zero
      return value === 0 ? 0 : 60 / value;
    default:
      return value;
  }
};

const convertFromSeconds = (
  seconds: number,
  unit: RuntimeDisplayUnits,
): number => {
  switch (unit) {
    case RUNTIME_DISPLAY_UNITS.HOURS:
      return seconds / 60 / 60;
    case RUNTIME_DISPLAY_UNITS.MINUTES:
      return seconds / 60;
    case RUNTIME_DISPLAY_UNITS.SECONDS:
      return seconds;
    case RUNTIME_DISPLAY_UNITS.PARTS_PER_HOUR:
      // Convert seconds per part to parts per hour protecting against division by zero
      return seconds === 0 ? 0 : (60 * 60) / seconds;
    case RUNTIME_DISPLAY_UNITS.PARTS_PER_MINUTE:
      // Convert seconds per part to parts per minute protecting against division by zero
      return seconds === 0 ? 0 : 60 / seconds;
    default:
      return seconds;
  }
};

export const safeConvertTime = (
  value: number | null,
  srcUnits: RuntimeDisplayUnits,
  dstUnits: RuntimeDisplayUnits,
  precision: number | null = null,
): string | number | null => {
  const parsed = parseDecimalString(value?.toString());

  if (parsed === '') {
    return '';
  }

  if (parsed === null || isNaN(parsed)) {
    return value;
  }

  const parsedNumber = parseFloat(parsed);
  const seconds = convertToSeconds(parsedNumber, srcUnits);
  const result = convertFromSeconds(seconds, dstUnits);

  if (precision !== null) {
    return result.toFixed(precision);
  }

  return result;
};

export const SPECIAL_VARIABLE_KEYS = {
  RUNTIME: 'runtime',
  SETUP_TIME: 'setup_time',
};

export const VALUE_TYPES = {
  NUMBER: 'number',
  CURRENCY: 'currency',
  STRING: 'string',
};

export const VARIABLE_CONTEXT_KEYS = {
  VARIABLE: 'variables',
  TABLE_VARIABLE: 'table_variables',
  DROP_DOWN_VARIABLE: 'drop_down_variables',
};

export const VARIABLE_TYPES = {
  STANDARD: 'standard',
  TABLE: 'table',
  DROP_DOWN: 'drop_down',
};

export const doOverridesExist = (
  overrides,
  quantities,
  displayContext = { variables: [] },
) => {
  const keysToIgnore = [
    SPECIAL_VARIABLE_KEYS.RUNTIME,
    SPECIAL_VARIABLE_KEYS.SETUP_TIME,
  ];
  for (const contextValue of displayContext.variables) {
    if (keysToIgnore.includes(contextValue.secondaryKey)) {
      continue;
    }
    const value = pathOr(
      null,
      [contextValue.primaryKey, contextValue.secondaryKey],
      overrides,
    );
    if (value == null) {
      continue;
    }

    if (contextValue.quantitySpecific) {
      if (typeof value === 'object') {
        for (const qty of quantities) {
          const qtySpecificVal = pathOr(null, [qty.toString()], value);
          if (qtySpecificVal != null) {
            return true;
          }
        }
      }
    } else {
      if (contextValue.variableType === VARIABLE_TYPES.TABLE) {
        if (typeof value === 'object' && 'row_number' in value) {
          return true;
        }
      } else {
        if (typeof value !== 'object') {
          return true;
        }
      }
    }
  }
  return false;
};

export const mapAutomaticValuesToOverrides = (displayContext, overrides) => {
  return displayContext.variables.map((variable) => {
    let override;
    if (variable.primaryKey in overrides) {
      if (variable.secondaryKey in overrides[variable.primaryKey]) {
        override = overrides[variable.primaryKey][variable.secondaryKey];
      } else if (
        variable.secondaryKey === SPECIAL_VARIABLE_KEYS.SETUP_TIME &&
        overrides[variable.primaryKey][variable.secondaryKey]
      ) {
        /**
         * TODO - this is a hack, secondary key is a string value so it does not get transformed from snake case to
         * camel case however when being passed back from the server it is used as and object key in overrides
         * so it is tranasformed to camelCase so this code checks to see if there is a camelCase version of secondary key
         * in overrides.
         */
        override = overrides[variable.primaryKey][variable.secondaryKey];
      }
    }
    return { ...variable, override };
  });
};

export const extractContextVariableGroups = (
  variablesContext,
  variableGroups,
) => {
  const out = {
    primary: {
      defaultCollapsed: false,
      vars: sortBy(prop('secondaryKey'))(
        variablesContext.filter(
          (variable) =>
            variable.primaryKey === VARIABLE_CONTEXT_KEYS.VARIABLE &&
            Object.values(SPECIAL_VARIABLE_KEYS).includes(
              variable.secondaryKey,
            ),
        ),
      ),
    },
  };
  const trackedKeys = Object.values(SPECIAL_VARIABLE_KEYS);
  for (const group of variableGroups) {
    out[group.name] = {
      defaultCollapsed: group.defaultCollapsed,
      vars: [],
    };
    for (const varName of group.variables) {
      const foundVar = variablesContext.find((v) => v.secondaryKey === varName);
      if (foundVar) {
        trackedKeys.push(foundVar.secondaryKey);
        out[group.name].vars.push(foundVar);
      }
    }
  }
  // remaining variables get put into declared group, sorted by position
  out.declared = {
    defaultCollapsed: false,
    vars: sortBy(prop('position'))(
      variablesContext.filter((v) => !trackedKeys.includes(v.secondaryKey)),
    ),
  };
  return out;
};

export const applyFormattersToVariableContext = (
  operation,
  variablesContext,
) => {
  return variablesContext.map((v) => {
    if (Object.values(SPECIAL_VARIABLE_KEYS).includes(v.secondaryKey)) {
      const displayUnitsFieldName = DISPLAY_UNITS_FIELD_NAMES[v.secondaryKey];
      return {
        ...v,
        inFormatter: (value, precision = 2) =>
          safeConvertTime(
            value,
            RUNTIME_DISPLAY_UNITS.HOURS,
            operation[displayUnitsFieldName] || RUNTIME_DISPLAY_UNITS.MINUTES,
            precision,
          ),
        outFormatter: (value, precision = null) =>
          safeConvertTime(
            value,
            operation[displayUnitsFieldName] || RUNTIME_DISPLAY_UNITS.MINUTES,
            RUNTIME_DISPLAY_UNITS.HOURS,
            precision,
          ),
      };
    } else {
      return {
        ...v,
        inFormatter: (value) => value,
        outFormatter: (value) => {
          if (
            v.valueType === VALUE_TYPES.CURRENCY ||
            v.valueType === VALUE_TYPES.NUMBER
          ) {
            return parseDecimalString(value);
          } else {
            return value;
          }
        },
      };
    }
  });
};

const DISPLAY_UNITS_LABELS = {
  [RUNTIME_DISPLAY_UNITS.SECONDS]: 'sec',
  [RUNTIME_DISPLAY_UNITS.MINUTES]: 'min',
  [RUNTIME_DISPLAY_UNITS.HOURS]: 'hours',
  [RUNTIME_DISPLAY_UNITS.PARTS_PER_HOUR]: 'p/hr',
  [RUNTIME_DISPLAY_UNITS.PARTS_PER_MINUTE]: 'p/min',
};

export const applyDisplayAnnotationsToVariableContext = (
  operation,
  variablesContext,
) => {
  return variablesContext.map((v) => {
    if (Object.values(SPECIAL_VARIABLE_KEYS).includes(v.secondaryKey)) {
      const displayUnitsFieldName = DISPLAY_UNITS_FIELD_NAMES[v.secondaryKey];
      return {
        ...v,
        prefix: '',
        suffix:
          ' ' +
          DISPLAY_UNITS_LABELS[
            operation[displayUnitsFieldName] || RUNTIME_DISPLAY_UNITS.MINUTES
          ],
      };
    } else if (v.valueType === VALUE_TYPES.CURRENCY) {
      return {
        ...v,
        prefix: '$',
        suffix: '',
      };
    } else {
      return {
        ...v,
        prefix: '',
        suffix: '',
      };
    }
  });
};

export const LIVE_PRICING_KEY_SEPARATOR = '$|%%|$';

export const constructLivePricingVariableKey = (variable, qty = null) => {
  if (qty !== null) {
    return `${variable.primaryKey}${LIVE_PRICING_KEY_SEPARATOR}${variable.secondaryKey}${LIVE_PRICING_KEY_SEPARATOR}${qty}`;
  } else {
    return `${variable.primaryKey}${LIVE_PRICING_KEY_SEPARATOR}${variable.secondaryKey}`;
  }
};

export const LIVE_PRICING_DRAWER_TYPES = {
  ADD_ON: 'ADD_ON',
  OPERATION: 'OPERATION',
  PROFIT_ITEM: 'PROFIT_ITEM',
  DISCOUNT: 'DISCOUNT',
} as const;

// Selects corresponding audit
export const parseCellAudit = (audits, key) => {
  return audits
    ? audits.find((a) => toCamelCase(a.key) === toCamelCase(key))
    : null;
};

// Selects corresponding audit for the correct quantity
export const parseCellAndQuantityAudit = (audits, key, quantity) => {
  return audits
    ? audits.find(
        (a) =>
          toCamelCase(a.key) === toCamelCase(key) &&
          (a.quantity?.toString() ?? null) === (quantity?.toString() ?? null),
      )
    : null;
};

// Calculates total add on price
export const totalRequiredAddOnPrice = (quantityId, addOnCells, addOns) => {
  const quantityAddOnCells = addOnCells.filter(
    (addOnCell) => addOnCell.quantityId === quantityId,
  );
  const requiredAddOns = quantityAddOnCells.filter(
    (addOnCell) =>
      addOns.find((addOn) => addOn.id === addOnCell.addOnId) &&
      addOns.find((addOn) => addOn.id === addOnCell.addOnId).isRequired,
  );
  let total = 0;
  for (const addOnCell of requiredAddOns) {
    total += Number(addOnCell.manualPrice ?? addOnCell.price);
  }
  return total;
};

export const getMinTotalPriceWithRequiredAddOns = (quoteItems) => {
  const getMin = (lstIn) => {
    const lst = lstIn.filter((x) => isFinite(x) && x >= 0);
    return lst.length > 0 ? Math.min(...lst) : 0;
  };
  const minimumPriceForEachQuoteItem = quoteItems.map((quoteItem) => {
    return quoteItem.rootComponent.quantities
      ? getMin(
          quoteItem.rootComponent.quantities.map((qty) => {
            return parseFloat(qty.totalPriceWithRequiredAddOns);
          }),
        )
      : 0;
  });
  return getMin(minimumPriceForEachQuoteItem);
};

export const getMaxTotalPriceWithRequiredAddOns = (quoteItems) => {
  const maximumPriceForEachQuoteItem = quoteItems.map((quoteItem) => {
    const maximumPriceForEachQuantity = quoteItem.rootComponent.quantities
      ? quoteItem.rootComponent.quantities
          .map((qty) => {
            const totalPriceWithRequiredAddOns = parseFloat(
              qty.totalPriceWithRequiredAddOns,
            );
            const requiredAddOnsPrice =
              qty.totalPriceWithRequiredAddOns - qty.totalPrice;
            const expeditePricesWithAddOns = qty.expedites.map(
              (expedite) => expedite.totalPrice + requiredAddOnsPrice,
            );
            return Math.max(
              0,
              totalPriceWithRequiredAddOns,
              ...expeditePricesWithAddOns,
            );
          })
          .filter(isFinite)
      : [0];
    return Math.max(0, ...maximumPriceForEachQuantity);
  });
  return maximumPriceForEachQuoteItem.reduce((acc, price) => acc + price, 0);
};

export const getMaxTotalPriceWithAllAddOns = (quoteItems) => {
  const maximumPriceForEachQuoteItem = quoteItems.map((quoteItem) => {
    const maximumPriceForEachQuantity = quoteItem.rootComponent.quantities
      ? quoteItem.rootComponent.quantities
          .map((qty) => {
            const totalPriceWithAllAddOns = parseFloat(
              qty.totalPriceWithAllAddOns,
            );
            const totalAddOnPrice =
              totalPriceWithAllAddOns - parseFloat(qty.totalPrice);
            const expeditePricesWithAddOns = qty.expedites.map(
              (expedite) => parseFloat(expedite.totalPrice) + totalAddOnPrice,
            );
            return Math.max(
              0,
              totalPriceWithAllAddOns,
              ...expeditePricesWithAddOns,
            );
          })
          .filter(isFinite)
      : [0];
    return Math.max(0, ...maximumPriceForEachQuantity);
  });
  return maximumPriceForEachQuoteItem.reduce((acc, price) => acc + price, 0);
};

export const getQuantityBreakInputSchema = (
  maxQuantities,
  isRequired = false,
) => {
  return mixed()
    .test(
      'quantities-content',
      'Must be comma separated list of positive integers.',
      (value) => {
        const subSchema = array()
          .ensure()
          .of(number().nullable(false).integer().moreThan(0));

        const valuesArray = splitQuantityString(value);
        return subSchema.isValid(valuesArray);
      },
    )
    .test(
      'quantities-count',
      `No more than ${maxQuantities} quantities.`,
      (value) => {
        const subSchema = array().ensure().max(maxQuantities);
        const valuesArray = splitQuantityString(value);
        return subSchema.isValid(valuesArray);
      },
    )
    .test(
      'quantities-no-duplicates',
      `Quantity list can't contain duplicates.`,
      (value) => {
        const valuesArray = splitQuantityString(value);
        const set = new Set(valuesArray);
        return set.size === valuesArray.length;
      },
    )
    .test('quantities-required', 'Quantities required', (value) => {
      const minQuantities = isRequired ? 1 : 0;
      const subSchema = array().ensure().min(minQuantities);
      const valuesArray = splitQuantityString(value, []);
      return subSchema.isValid(valuesArray);
    });
};
